import dynamic from 'next/dynamic';

import { Link } from '@natu/next-link';
import { type ButtonProps } from '@natu/ui/Button';
import type { Icon } from '@natu/ui/DynamicIcon';
import {
  type TypographyVariant,
  type TypographyVariantProp,
  getTypographyVariantStyles,
} from '@natu/ui/Typography';
import { cn } from '@natu/utils/cn';
import type { PredefinedGAEvents } from '@natu/utils/events';

import {
  FontWeight,
  SBProps,
  Spacing,
  StoryblokLink,
  getDescriptiveText,
  getLinkPropsFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
} from '../../../../utils';

const DynamicIcon = dynamic(() => import('@natu/ui/DynamicIcon').then(mod => mod.DynamicIcon));

const Button = dynamic(() => import('@natu/ui/Button').then(mod => mod.Button));

const ButtonWithEvent = dynamic(() =>
  import('@natu/ui/Button/ButtonWithEvent').then(mod => mod.ButtonWithEvent),
);

type SBButtonProps = {
  event?: PredefinedGAEvents;
  event_cta_position?: string;
  event_cta_section?: string;
  content?: string;
  link?: StoryblokLink;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  fullWidth?: boolean;
  fontWeight?: FontWeight;
  iconLeft?: Icon;
  iconRight?: Icon;
  technologyIcon?: Icon;
  fontSizeMobile?: TypographyVariant;
  fontSizeTablet?: TypographyVariant;
  fontSizeDesktop?: TypographyVariant;
  descriptiveText?: string;
};
export const SBCta = ({ blok }: SBProps<SBButtonProps>) => {
  const {
    event,
    event_cta_position,
    event_cta_section,
    content,
    size,
    link,
    variant,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
    fullWidth,
    iconLeft,
    iconRight,
    technologyIcon,
    fontWeight,
    fontSizeMobile,
    fontSizeTablet,
    fontSizeDesktop,
  } = blok;

  const linkProps = getLinkPropsFromStoryblok(link);

  if (!linkProps.href) {
    return null;
  }

  const { descriptiveText } = getDescriptiveText({
    title: content,
    link: linkProps,
  });

  const fontSizeVariant: TypographyVariantProp = [
    fontSizeMobile || 'text-sm-13',
    fontSizeTablet || fontSizeMobile,
    fontSizeDesktop || fontSizeTablet || fontSizeMobile,
  ];

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
    fontWeight: fontWeight || 'semibold',
    className: cn(fullWidth ? 'w-full' : 'w-max', getTypographyVariantStyles(fontSizeVariant)),
  });

  const buttonProps: ButtonProps = {
    asChild: true,
    className: className,
    size: size,
    title: content,
    variant: variant || 'primary',
    'aria-label': descriptiveText || content,
  };

  const ButtonContent = () => (
    <>
      {iconLeft && <DynamicIcon className={cn('h-5 w-5', content && 'mr-2')} icon={iconLeft} />}
      {technologyIcon && (
        <DynamicIcon
          className={cn('size-5 brightness-0 contrast-100 grayscale', content && 'mr-2')}
          icon={technologyIcon}
        />
      )}
      {content}
      {iconRight && <DynamicIcon className={cn('h-5 w-5', content && 'ml-2')} icon={iconRight} />}
    </>
  );

  if (event)
    return (
      <ButtonWithEvent
        event={event as PredefinedGAEvents}
        eventProps={{
          event_cta_position,
          event_cta_section,
          text: content,
        }}
        {...buttonProps}
        {...sbEditable(blok)}
      >
        <Link {...linkProps}>
          <ButtonContent />
        </Link>
      </ButtonWithEvent>
    );

  return (
    <Button {...buttonProps} {...sbEditable(blok)}>
      <Link {...linkProps}>
        <ButtonContent />
      </Link>
    </Button>
  );
};
